/**
 * MasterContactModal Component
 * --------------------------------------------
 * Purpose:
 * This modal allows the SUPER ADMIN to:
 *   ✔ Upload a Master Contacts CSV file
 *   ✔ View previously uploaded master contact import logs
 *   ✔ Download a CSV template based on master contact header configuration
 *
 * FEATURES:
 *  - Displays list of previously uploaded master contact jobs with:
 *      • Request ID
 *      • Created date
 *      • Upload count
 *      • Uploaded file link
 *      • Processed file link (if available)
 *  - User can upload a new .csv / .xlsx / .xls file
 *  - CSV template generation using CSVGenerator
 *
 * PROPS:
 *  open       → Boolean controlling modal state
 *  close      → Function to close modal
 *  theme      → Boolean (light/dark) for theme-based styling
 *
 * API Used:
 *  fetchMasterContactsFile() → Fetches upload logs list
 *  uploadMasterContactsFile() → Uploads a new master contacts file
 */

import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import { fetchMasterContactsFile, uploadMasterContactsFile } from "../../../Services/contacts";
import { useDispatch, connect } from "react-redux";
import moment from "moment";
import { themeColors } from "../../theme";
import CSVGenerator from "./CSVGenerator";
import "../../../theme-vars.css";
import { useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import { Circles } from "react-loader-spinner";
import { conatctHeader } from "../../../Values/masterHeader";

type props = {
  open: boolean;
  close: () => void;
  history: any;
  theme: any;
};

interface DataObject {
  [key: string]: any;
}

const MasterContactModal: FunctionComponent<props> = ({
  open,
  close,
  theme
}) => {

  /** MUI theme hook for responsive styling */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const filtervalues = ["customer_name", "country_code", "contact_no", "alternate_no", "email", "stage", "contact_owner_email", "lead_source", "budget", "call_back_reason", "project", "property_type", "property_sub_type", "property_stage", "location", "lost_reason", "not_int_reason", "other_lost_reason", "other_not_int_reason", "next_follow_up_type", "next_follow_up_date_time", "created_by", "created_at", "lead_assign_time", "stage_change_at", "addset", "campaign", "notes", "lead_type", "field_one", "field_two", "field_three", "field_four", "field_five", "field_six"];
  const reqData = conatctHeader.filter((obj: any) => filtervalues.includes(obj.label));

  /**
   * Build final CSV template headers
   * Includes:
   *  - Organization Name
   *  - All required contact mapping headers
   *  - Notes field at the end
   */
  const dbData = [
    { label: "organization_name", value: "Organization Name" },
    ...reqData,
    { label: "notes", value: "Notes" }
  ];

  const headerData = dbData.map((item: any) => item.value);
  const contactHeaderData: DataObject[] = [];
  contactHeaderData.push(headerData);
  const [load, setLoad] = useState(false);

  /** Stores previously uploaded master contact file logs */
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

  const dispatcher = useDispatch();

  /**
   * FETCH PREVIOUS UPLOAD LOGS
   * --------------------------------------------------------
   * On initial load, fetch list of uploaded master contact files.
   */
  useEffect(() => {
    const unsub = fetchMasterContactsFile(
      " ", // master organization placeholder
      (data: any) => setUploadedFiles(data),
      dispatcher
    );
    return () => {
      unsub();
    };
  }, []);

  /**
   * UPLOAD NEW MASTER CONTACT FILE
   * --------------------------------------------------------
   * Handles file upload and triggers backend processing.
   */
  const upload = async (file: any) => {
    setLoad(true);
    uploadMasterContactsFile(
      file,
      " ", // master organization placeholder
      dispatcher,
      (data: boolean) => setLoad(data),
      "Super Admin" // uploaded by information
    );
  };

  /**
   * Theme-responsive table styling
   */
  const tdStyles = {
    backgroundColor: theme ? themeColors.tdBackgroundColor : themeColors.tdBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  };

  const thStyles = {
    backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark
  }

  /**
   * React Modal Styling
   */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme ? themeColors.cardBackgroundColor : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "10px 20px",
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      margin: "auto",
      inset: "auto",
    },
  };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <div className={styles.child}>

          {/* MODAL TITLE */}
          <h5
            style={{
              fontFamily: "var(--font-family-main)",
              fontSize: "var(--font-size-large)",
              paddingTop: isMobile ? "unset" : "15px"
            }}
          >
            Import Data for "Contact Form"
          </h5>

          {/* UPLOADED FILES TABLE */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead className={styles.head} style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}>
                <tr>
                  <th className={styles.th} style={thStyles}>Sno.</th>
                  <th className={styles.th} style={thStyles}>Request Id</th>
                  <th className={styles.th} style={thStyles}>Created At</th>
                  <th className={styles.th} style={thStyles}>Status</th>
                  <th className={styles.th} style={thStyles}>Upload Count</th>
                  <th className={styles.th} style={thStyles}>Uploaded File</th>
                  <th className={styles.th} style={thStyles}>Processed File</th>
                </tr>
              </thead>

              <tbody>
                {uploadedFiles.map((item: any, index: number) => (
                  <tr key={index} className={styles.no}>
                    <td className={styles.td} style={tdStyles}>{index + 1}</td>
                    <td className={styles.td} style={tdStyles}>{item.request_id}</td>

                    {/* Format Firebase Timestamp */}
                    <td className={styles.td} style={tdStyles}>
                      {moment(item.created_at.toDate()).format("MM/DD/YY HH:mm")}
                    </td>

                    <td className={styles.td} style={tdStyles}>{item.status}</td>
                    <td className={styles.td} style={tdStyles}>{item.uploadCount}</td>

                    {/* Uploaded File URL */}
                    <td className={styles.td} style={tdStyles}>
                      <a href={item.fileUrl}>Uploaded File</a>
                    </td>

                    {/* Processed File URL (only if exists) */}
                    <td className={styles.td} style={tdStyles}>
                      {item.responseURL !== "" && (
                        <a href={item.responseURL}>Processed File</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ACTION BUTTONS SECTION */}
          <div className={styles.selectBox}>
            <div style={{ marginRight: 8 }}>
              <StyledCancelButton onClick={close}>Cancel</StyledCancelButton>
            </div>

            {/* CSV TEMPLATE DOWNLOAD */}
            <div style={{ marginRight: 8 }}>
              <CSVGenerator data={contactHeaderData} />
            </div>

            {/* FILE UPLOAD BUTTON */}
            <div>
              <input
                id="file-input"
                className={styles.select}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e: any) => upload(e.target.files[0])}
              />

              <StyledSubmitButton
                onClick={() => document.getElementById("file-input")?.click()}
              >
                {load ? (
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <Circles color="#fff" height={20} width={20} /> Upload...
                  </span>
                ) : (
                  <>Upload File</>
                )}
              </StyledSubmitButton>
            </div>
          </div>

          {/* FOOTER INFO TEXT */}
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <p style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}>
              <i className="fas fa-info-circle"></i> Only .csv file type is supported.
            </p>
            <p
              style={{
                marginTop: "-1rem",
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-small)"
              }}
            >
              <i className="fas fa-info-circle"></i> Please ensure filling all the mandatory fields.
            </p>
          </div>

        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(MasterContactModal);
